import {
  createStore,
  applyMiddleware,
  compose,
  StoreEnhancerStoreCreator,
  combineReducers,
  Store,
  Reducer,
  Action,
  ReducersMapObject
} from 'redux';
import axios from 'axios';
import { createLogicMiddleware } from 'redux-logic';
import getConfig from 'next/config';

import customThunk from './middleware/customThunk';
import analyticsMiddleware from './middleware/AnalyticsMiddleware';
import Middleware from './middleware/Middleware';
import RiskMiddleware from './middleware/RiskMiddleware';
import appBridgeMiddleware from './middleware/app-bridge/AppBridgeMiddleware';
import appLogic from './app/logic';
import { setupInterceptors } from '../helpers';

let reducers: ReducersMapObject<any, any> = {};

export type Dependencies = {
  httpClient: ReturnType<typeof axios.create>;
  publicRuntimeConfig: ReturnType<typeof getConfig>;
};

export const setReducer = (key, reducer) => {
  reducers[key] = reducer;
  return reducers;
};

export interface StoreWithInjectReducer<StateType> extends Store {
  injectReducer(
    key: keyof StateType,
    reducer: (
      preloadedState?: StateType[keyof StateType]
    ) => Reducer<StateType[keyof StateType]>
  ): void;
}

export default function configureStore<StateType>(
  initialState: Partial<StateType>
): StoreWithInjectReducer<StateType> {
  const { publicRuntimeConfig } = getConfig();

  let options = {};
  if (publicRuntimeConfig.NEXT_PUBLIC_NODE_ENV === 'local') {
    options = { timeout: 30000 };
  }

  const axiosClient = axios.create(options);
  setupInterceptors(axiosClient);
  const composeEnhancers: typeof compose =
    (publicRuntimeConfig.nodeEnv !== 'production' &&
      typeof window === 'object' &&
      (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) ||
    compose;

  const logicMiddleware = createLogicMiddleware(appLogic, {
    httpClient: axiosClient,
    publicRuntimeConfig
  });

  const enhancers = composeEnhancers<StoreEnhancerStoreCreator<StateType>>(
    applyMiddleware(logicMiddleware),
    applyMiddleware(analyticsMiddleware),
    applyMiddleware(Middleware),
    applyMiddleware(RiskMiddleware),
    applyMiddleware(appBridgeMiddleware),
    f => f
  );

  const store = createStore<StateType, Action<any>, unknown, unknown>(
    (state = (initialState || {}) as StateType) => state,
    enhancers
  );

  const injectReducer = (
    key: keyof StateType,
    initializeReducer: (preloadedState?: StateType[typeof key]) => Reducer
  ) => {
    if (!reducers[key]) {
      const reducer = initializeReducer(initialState[key]);
      reducers = setReducer(key, reducer);
      store.replaceReducer(combineReducers(reducers));
    }
  };

  return { ...store, injectReducer };
}
